import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl, getJsonAuthHeaders } from '../../../utils/utils';

type OrderTab = "prescriptions" | "lab-orders" | "imaging" | "new";

interface Prescription {
    id: string; patient_name: string; medication: string; dosage: string;
    frequency: string; duration: string; status: string; prescribed_date: string;
    refills_remaining: number; pharmacy: string;
}

interface LabOrder {
    id: string; patient_name: string; test_name: string; urgency: string;
    status: string; ordered_date: string; facility: string;
}

const PrescriptionsOrders: React.FC = () => {
    const [activeTab, setActiveTab] = useState<OrderTab>('prescriptions');
    const [searchQuery, setSearchQuery] = useState('');
    const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
    const [labOrders, setLabOrders] = useState<LabOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => { document.title = "Uzima - Prescriptions & Orders"; loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const headers = await getJsonAuthHeaders();
            const [rxRes, labRes] = await Promise.allSettled([
                fetch(`${baseUrl}core/api/prescriptions/`, { headers }),
                fetch(`${baseUrl}core/api/lab-orders/`, { headers }),
            ]);
            if (rxRes.status === 'fulfilled' && rxRes.value.ok) { const d = await rxRes.value.json(); if (d.status === 'success') setPrescriptions(d.data || []); }
            if (labRes.status === 'fulfilled' && labRes.value.ok) { const d = await labRes.value.json(); if (d.status === 'success') setLabOrders(d.data || []); }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const activeRx = prescriptions.filter(r => r.status === 'active');
    const pendingLab = labOrders.filter(o => o.status !== 'completed');

    const tabs: Array<{ key: OrderTab; label: string; icon: string }> = [
        { key: "prescriptions", label: "Prescriptions", icon: "bx bx-capsule" },
        { key: "lab-orders", label: "Lab Orders", icon: "bx bx-test-tube" },
        { key: "imaging", label: "Imaging", icon: "bx bx-scan" },
        { key: "new", label: "New Order", icon: "bx bx-plus" },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Prescriptions & Orders</h1><p>Manage prescriptions, lab orders, and imaging requests</p></div>
                            <button className="sc-btn sc-btn-primary" onClick={() => setActiveTab('new')}><i className="bx bx-plus"></i> New Order</button>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-capsule"></i></div><div className="sc-stat-value">{activeRx.length}</div><div className="sc-stat-label">Active Rx</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-test-tube"></i></div><div className="sc-stat-value">{pendingLab.length}</div><div className="sc-stat-label">Pending Labs</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-scan"></i></div><div className="sc-stat-value">{prescriptions.length}</div><div className="sc-stat-label">Total Rx</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-list-ul"></i></div><div className="sc-stat-value">{labOrders.length}</div><div className="sc-stat-label">Total Orders</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'prescriptions' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Prescriptions</h3></div>
                                        <div className="sc-panel-body">
                                            <div className="sc-toolbar"><div className="sc-search"><i className="bx bx-search"></i><input type="text" placeholder="Search prescriptions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div></div>
                                            {prescriptions.length === 0 ? (<div className="sc-empty"><i className="bx bx-capsule"></i><h3>No Prescriptions</h3><p>Prescriptions will appear here when created.</p></div>) : (
                                                <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Rx ID</th><th>Patient</th><th>Medication</th><th>Dosage</th><th>Frequency</th><th>Status</th><th>Pharmacy</th></tr></thead>
                                                    <tbody>{prescriptions.map(rx => (<tr key={rx.id}><td><span className="sc-chip soft">{rx.id}</span></td><td>{rx.patient_name}</td><td><strong>{rx.medication}</strong></td>
                                                        <td>{rx.dosage}</td><td>{rx.frequency}</td><td><span className={`sc-badge-status ${rx.status === 'active' ? 'agreed' : rx.status === 'pending' ? 'pending' : 'expired'}`}>{rx.status}</span></td><td>{rx.pharmacy}</td></tr>))}</tbody></table></div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'lab-orders' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Lab Orders</h3></div>
                                        <div className="sc-panel-body">{labOrders.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-test-tube"></i><h3>No Lab Orders</h3><p>Lab orders will appear here when placed.</p></div>
                                        ) : (
                                            <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Order ID</th><th>Patient</th><th>Test</th><th>Urgency</th><th>Status</th><th>Ordered</th><th>Facility</th></tr></thead>
                                                <tbody>{labOrders.map(o => (<tr key={o.id}><td><span className="sc-chip soft">{o.id}</span></td><td>{o.patient_name}</td><td>{o.test_name}</td>
                                                    <td><span className={`sc-chip ${o.urgency === 'stat' ? 'soft' : ''}`}>{o.urgency}</span></td>
                                                    <td><span className={`sc-badge-status ${o.status === 'completed' ? 'agreed' : 'pending'}`}>{o.status}</span></td><td>{o.ordered_date}</td><td>{o.facility}</td></tr>))}</tbody></table></div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'imaging' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-scan"></i><h3>Imaging Orders</h3><p>X-Ray, MRI, CT Scan, and Ultrasound requests sent to partner imaging centers across the network.</p>
                                        <button className="sc-btn sc-btn-primary" style={{ marginTop: '12px' }}><i className="bx bx-plus"></i> New Imaging Order</button></div></div></div>
                                )}
                                {activeTab === 'new' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Create New Order</h3></div>
                                        <div className="sc-panel-body"><div className="sc-grid">{[
                                            { label: 'E-Prescription', desc: 'Prescribe medication for a patient', icon: 'bx bx-capsule' },
                                            { label: 'Lab Order', desc: 'Order lab tests from partner labs', icon: 'bx bx-test-tube' },
                                            { label: 'Imaging Order', desc: 'Request imaging from partner centers', icon: 'bx bx-scan' },
                                            { label: 'Procedure Order', desc: 'Order a medical procedure', icon: 'bx bx-first-aid' },
                                        ].map((item, i) => (
                                            <div className="sc-card" key={i} style={{ cursor: 'pointer', textAlign: 'center' }}><div className="sc-card-body">
                                                <div className="sc-stat-icon teal" style={{ margin: '0 auto 12px' }}><i className={item.icon}></i></div>
                                                <div className="sc-card-title">{item.label}</div><div className="sc-card-desc">{item.desc}</div>
                                            </div></div>
                                        ))}</div></div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default PrescriptionsOrders;
